import { useState, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { ApiError } from '../api';
import { collapseDoubledUrl } from '../utils/collapseDoubledUrl';
import * as adminStoryRepository from '../repositories/adminStoryRepository';
import { recordEvent } from '../repositories/storyRepository';
import * as listRepository from '../repositories/listRepository';
import { ModalOverlay } from './ModalOverlay';

function TierPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="add-link-tier-picker">
      {[1, 2, 3, 4, 5].map((n) => {
        const active = value === String(n);
        return (
          <button
            key={n}
            type="button"
            onClick={() => onChange(active ? '' : String(n))}
            className={`add-link-tier-btn${active ? ' active' : ''}`}
          >
            {n}
          </button>
        );
      })}
    </div>
  );
}

interface AddLinkModalProps {
  isAdmin: boolean;
}

export function AddLinkModal({ isAdmin }: AddLinkModalProps) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [tier, setTier] = useState('');
  const [note, setNote] = useState('');
  const [addToFlagged, setAddToFlagged] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<{ message: ReactNode; isError: boolean }>({ message: '', isError: false });
  const [pasteHint, setPasteHint] = useState(false);
  const [allLists, setAllLists] = useState<string[]>([]);
  const [selectedLists, setSelectedLists] = useState<string[]>([]);
  const [newListName, setNewListName] = useState('');
  const urlRef = useRef<HTMLInputElement>(null);
  const openRef = useRef(false);

  useEffect(() => { openRef.current = open; }, [open]);

  useEffect(() => {
    if (!isAdmin) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
      }
    };
    const onCustom = () => setOpen(true);
    const onPaste = (e: ClipboardEvent) => {
      if (openRef.current) return;
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
      const text = e.clipboardData?.getData('text') ?? '';
      if (/^https?:\/\/.+/.test(text.trim())) {
        e.preventDefault();
        setUrl(text.trim());
        setPasteHint(true);
        setOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('open-add-link-modal', onCustom);
    document.addEventListener('paste', onPaste);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('open-add-link-modal', onCustom);
      document.removeEventListener('paste', onPaste);
    };
  }, [isAdmin]);

  useEffect(() => {
    if (open) {
      // Select any auto-filled URL so a re-paste replaces it instead of
      // appending (which used to produce a doubled `URL+URL` value).
      setTimeout(() => {
        urlRef.current?.focus();
        urlRef.current?.select();
      }, 50);
      listRepository.getUserLists().then(setAllLists).catch(() => {});
    }
  }, [open]);

  const handleClose = () => {
    if (submitting) return;
    setOpen(false);
    reset();
  };

  const reset = () => {
    setUrl('');
    setTitle('');
    setTier('');
    setNote('');
    setAddToFlagged(true);
    setSelectedLists([]);
    setNewListName('');
    setStatus({ message: null, isError: false });
    setPasteHint(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedUrl = collapseDoubledUrl(url);
    if (!trimmedUrl) return;
    setSubmitting(true);
    setStatus({ message: '', isError: false });
    try {
      // One call does the whole ingest server-side (content fetch + enrichment
      // queue included). No title means the fetched page title is used.
      const story = await adminStoryRepository.createStory({
        title: title.trim() || undefined,
        url: trimmedUrl,
        tier: tier ? parseInt(tier, 10) : undefined,
        note: note.trim() || undefined,
      });
      const alreadyExisted = (story as unknown as Record<string, unknown>).already_existed === true;
      if (addToFlagged) await recordEvent(story.id, 'flag');
      const listsToAdd = [...selectedLists];
      if (newListName.trim() && !listsToAdd.includes(newListName.trim())) {
        listsToAdd.push(newListName.trim());
      }
      await Promise.all(listsToAdd.map((tag) => listRepository.addArticleToList(story.id, tag)));
      if (alreadyExisted) {
        const actions: string[] = [];
        if (addToFlagged) actions.push('bookmarked');
        if (listsToAdd.length > 0) actions.push(`added to ${listsToAdd.length} list${listsToAdd.length > 1 ? 's' : ''}`);
        if (tier) actions.push(`tier set to ${tier}`);
        if (note.trim()) actions.push('note saved');
        const actionSummary = actions.length > 0 ? ` ${actions.join(', ')}.` : '';
        setStatus({
          message: <>Article already existed.{actionSummary} <a href={`/admin/article/${story.id}`} target="_blank" rel="noreferrer">View article</a></>,
          isError: false,
        });
      } else {
        const enrichmentQueued = (story as unknown as Record<string, unknown>).enrichment_queued === true;
        setStatus({
          message: enrichmentQueued ? 'Article added and queued for enrichment.' : 'Article added.',
          isError: false,
        });
      }
      setTimeout(() => {
        setOpen(false);
        reset();
      }, 1400);
    } catch (err) {
      setStatus({ message: (err as Error).message || 'Failed to add article', isError: true });
      setSubmitting(false);
    }
  };

  if (!isAdmin || !open) return null;

  return (
    <ModalOverlay onClose={handleClose} className="add-link-modal">
        {pasteHint && (
          <div className="add-link-paste-hint">
            It looks like you're trying to paste a URL — do you want to add this article?
          </div>
        )}
        <div className="add-link-modal-header">
          <div>
            <h2 className="add-link-modal-title">Add article</h2>
            <p className="add-link-modal-subtitle">Manually add a link to the article pool</p>
          </div>
          <button type="button" className="add-link-close-btn" onClick={handleClose} aria-label="Close">
            ✕
          </button>
        </div>
        <form className="add-link-form" onSubmit={handleSubmit}>
          <label className="add-link-label">
            <span>URL <span className="add-link-required">*</span></span>
            <input
              ref={urlRef}
              type="url"
              className="add-link-input"
              placeholder="https://example.com/article"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              disabled={submitting}
              required
            />
          </label>
          <label className="add-link-label">
            <span>Title <span className="add-link-optional">optional</span></span>
            <input
              type="text"
              className="add-link-input"
              placeholder="Defaults to URL until enriched"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={submitting}
            />
          </label>
          <div className="add-link-label">
            <span>Tier <span className="add-link-optional">optional</span></span>
            <TierPicker value={tier} onChange={setTier} />
          </div>
          <label className="add-link-label">
            <span>Curation note <span className="add-link-optional">optional</span></span>
            <textarea
              className="add-link-textarea"
              placeholder="Why is this good or less good?"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={submitting}
              rows={3}
            />
          </label>
          <label className="add-link-flag-toggle">
            <input
              type="checkbox"
              checked={addToFlagged}
              onChange={(e) => setAddToFlagged(e.target.checked)}
              disabled={submitting}
            />
            Add to my bookmarks
          </label>
          <div className="add-link-label">
            <span>Add to list <span className="add-link-optional">optional</span></span>
            <div className="add-link-list-picker">
              {allLists.length > 0 && (
                <div className="add-link-list-items">
                  {allLists.map((tag) => (
                    <label key={tag} className="add-link-list-item">
                      <input
                        type="checkbox"
                        checked={selectedLists.includes(tag)}
                        onChange={() =>
                          setSelectedLists((prev) =>
                            prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
                          )
                        }
                        disabled={submitting}
                      />
                      <span>{tag}</span>
                    </label>
                  ))}
                </div>
              )}
              <input
                type="text"
                className="add-link-input"
                placeholder="New list..."
                value={newListName}
                maxLength={listRepository.LIST_NAME_MAX_LENGTH}
                onChange={(e) => setNewListName(e.target.value)}
                disabled={submitting}
              />
            </div>
          </div>
          {status.message && (
            <div className={status.isError ? 'add-link-status error' : 'add-link-status success'}>
              {status.message}
            </div>
          )}
          <div className="add-link-footer">
            <button type="button" className="secondary-button" onClick={handleClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="primary-button" disabled={submitting || !url.trim()}>
              {submitting ? <><span className="spinner" /> Adding…</> : 'Add article'}
            </button>
          </div>
        </form>
    </ModalOverlay>
  );
}
